import { Dimensions, PixelRatio, Platform, ScaledSize } from 'react-native';

import { isTablet as defineIsTablet } from 'react-native-device-info';
import { initialWindowMetrics } from 'react-native-safe-area-context';

export const isTablet = defineIsTablet();
export const isWeb = Platform.OS === 'web';
export const isPhone = !isTablet && !isWeb;
export const isTabletOrWeb = isTablet || isWeb;
export const isAndroid15 = Platform.OS === 'android' && Platform.Version >= 35;

export const designSizes = {
    phone: {
        height: 640,
        width: 360,
    },
    tablet: {
        height: 640,
        width: 960,
    },
    desktop: {
        height: 720,
        width: 1280,
    },
};

export function getPercentageWidth(widthInPx: number) {
    const { width } = Platform.select({
        web: designSizes.desktop,
        android: isTablet ? designSizes.tablet : designSizes.phone,
        ios: isTablet ? designSizes.tablet : designSizes.phone,
        default: designSizes.tablet,
    });

    return (widthInPx * 100) / width;
}

export function getPercentageHeight(heightInPx: number) {
    const { height } = Platform.select({
        web: designSizes.desktop,
        android: isTablet ? designSizes.tablet : designSizes.phone,
        ios: isTablet ? designSizes.tablet : designSizes.phone,
        default: designSizes.tablet,
    });

    return (heightInPx * 100) / height;
}

/**
 * Fork of react-native-responsive-screen
 *
 * https://github.com/marudy/react-native-responsive-screen
 */

// For calculating styles based on the device required orientation, not on the initial orientation
export const getScreenSize = (dimension: 'width' | 'height') => {
    const initialScreenWidth: ScaledSize['width'] =
        Dimensions.get('window').width;
    const initialScreenHeight: ScaledSize['height'] =
        Dimensions.get('window').height;
    const bigSide = Math.max(initialScreenWidth, initialScreenHeight);
    const smallSide = Math.min(initialScreenWidth, initialScreenHeight);

    if (isTabletOrWeb) {
        return dimension === 'width' ? bigSide : smallSide;
    }

    return dimension === 'width' ? smallSide : bigSide;
};

// Retrieve screen's width
export const screenWidth: ScaledSize['width'] = getScreenSize('width');
// Retrieve screen's height
export const screenHeight: ScaledSize['height'] = getScreenSize('height');

/**
 * Converts provided width percentage to independent pixel (dp).
 * @param  {number} widthInPxForPhone
 * The width of the element for phone on the Figma design in pixels.
 * @param  {number=} widthInPxForTablet
 * The width of the element for tablet on the Figma design in pixels.
 * @param  {number=} widthInPxForDesktop
 * The width of the element for desktop on the Figma design in pixels.
 *
 * @return {number}
 * The calculated dp depending on current device's screen width.
 */
export function widthPercentageToDP(
    widthInPxForPhone: number,
    widthInPxForTablet?: number,
    widthInPxForDesktop?: number,
) {
    const elemWidth = getPercentageWidth(
        Platform.select({
            web: widthInPxForDesktop ?? widthInPxForPhone,
            android: isTablet
                ? widthInPxForTablet ?? widthInPxForPhone
                : widthInPxForPhone,
            ios: isTablet
                ? widthInPxForTablet ?? widthInPxForPhone
                : widthInPxForPhone,
            default: widthInPxForPhone,
        }),
    );

    // Use PixelRatio.roundToNearestPixel
    // method in order to round the layout
    // size (dp) to the nearest one
    // that corresponds to an integer number of pixels.
    return PixelRatio.roundToNearestPixel((screenWidth * elemWidth) / 100);
}

/**
 * Converts provided height percentage to independent pixel (dp).
 * @param  {number} heightInPxForPhone
 * The height of the element for phone on the Figma design in pixels.
 * @param  {number=} heightInPxForTablet
 * The height of the element for tablet on the Figma design in pixels.
 *
 * @return {number}
 * The calculated dp depending on current device's screen height.
 */
export function heightPercentageToDP(
    heightInPxForPhone: number,
    heightInPxForTablet?: number,
    heightInPxForDesktop?: number,
) {
    const elemHeight = getPercentageHeight(
        Platform.select({
            web: heightInPxForDesktop ?? heightInPxForPhone,
            android: isTablet
                ? heightInPxForTablet ?? heightInPxForPhone
                : heightInPxForPhone,
            ios: isTablet
                ? heightInPxForTablet ?? heightInPxForPhone
                : heightInPxForPhone,
            default: heightInPxForPhone,
        }),
    );

    // Use PixelRatio.roundToNearestPixel
    // method in order to round the layout
    // size (dp) to the nearest one
    // that corresponds to an integer number of pixels.
    return PixelRatio.roundToNearestPixel((screenHeight * elemHeight) / 100);
}

// Often used aspect ratios
export const landscapeAspectRatio = 16 / 9;
export const portraitAspectRatio = 2 / 3;

/**
 * Calculate provided height and width to aspect ratio.
 * @param  {number} widthForPhone
 * The width of the element for phone on the Figma design in pixels.
 * @param  {number} heightForPhone
 * The height of the element for tablet on the Figma design in pixels.
 * @param  {number=} widthForTablet
 * The height of the element for tablet on the Figma design in pixels.
 * @param  {number=} heightForTablet
 * The height of the element for tablet on the Figma design in pixels.
 *
 * @return {number}
 * The calculated aspect ratio.
 */
export function dimensionsToAspectRatio(
    widthForPhone: number,
    heightForPhone: number,
    widthForTablet?: number,
    heightForTablet?: number,
): number {
    if (isTablet && widthForTablet && heightForTablet) {
        return widthForTablet / heightForTablet;
    }

    return widthForPhone / heightForPhone;
}
const userAgentPlatform =
    isWeb &&
    // @ts-ignore
    (window.navigator.userAgentData?.platform?.toLowerCase() ??
        window.navigator.platform.toLowerCase());
const isWindows =
    typeof userAgentPlatform === 'string' && userAgentPlatform.includes('win');

export const webDensityCoef =
    isWindows && window.devicePixelRatio > 1 ? window.devicePixelRatio : 1;

const userAgent = isWeb && window.navigator.userAgent;
// Mozilla Firefox uses viewport instead of screen sizes in screen.width and screen.height
const isFirefox =
    typeof userAgent === 'string' && userAgent.match(/firefox|fxios/i);

const webScreenWidth = isWeb ? window.screen.width : 0;
const webScreenHeight = isWeb ? window.screen.height : 0;
const zoomWidthCoefficient = isWeb ? webScreenWidth / window.innerWidth : 0;
const zoomHeightCoefficient = isWeb ? webScreenHeight / window.innerHeight : 0;
const correctedZoomWidthCoefficient = isWindows
    ? zoomWidthCoefficient
    : Math.ceil(zoomWidthCoefficient);
const correctedZoomHeightCoefficient = isWindows
    ? zoomHeightCoefficient
    : Math.ceil(zoomHeightCoefficient);
const correctedFullScreenWidth = isFirefox
    ? Math.floor(
          (webScreenWidth * window.devicePixelRatio) /
              correctedZoomWidthCoefficient,
      )
    : webScreenWidth;
const correctedFullScreenHeight = isFirefox
    ? Math.floor(
          (webScreenHeight * window.devicePixelRatio) /
              correctedZoomHeightCoefficient,
      )
    : webScreenHeight;
export const isMobileMediaQuery =
    isWeb &&
    (correctedFullScreenWidth < 600 || correctedFullScreenHeight < 600);
export const isTabletMediaQuery =
    isWeb &&
    (correctedFullScreenWidth <= 1024 ||
        (Number.isInteger(window.orientation) &&
            (correctedFullScreenHeight <= 1024 || window.innerHeight <= 1024)));

export const isWebMinWidthMediaQuery =
    (isWeb &&
        (correctedFullScreenWidth > 1024 ||
            correctedFullScreenWidth === window.innerWidth) &&
        window.innerWidth <= 1024 / webDensityCoef) ||
    (correctedFullScreenWidth > 1024 && window.innerHeight < 450);

export const isSafari =
    isWeb &&
    // @ts-ignore
    (/constructor/i.test(window.HTMLElement) ||
        (function (p) {
            return p.toString() === '[object SafariRemoteNotification]';
        })(
            // @ts-ignore
            !window.safari ||
                // @ts-ignore
                // eslint-disable-next-line no-undef
                (typeof safari !== 'undefined' && safari.pushNotification),
        ));

export const androidScreenMarginBottom = isAndroid15
    ? initialWindowMetrics?.insets.bottom
    : 0;
